import React from 'react';
import { filter } from 'lodash';
import Link from '@mui/material/Link';
import SelectTypeAhead, { Option } from '../components/form-elements/select-type-ahead';
import Alert from '../components/alert';
import linkTo, { templates } from '../utils/linkTo';
import DelayedTextInput from '../shared/delayed-text-input';
import Button from '../shared/button';
import ButtonActions from '../shared/button-actions';
import { Workflow } from '../workflow-builder/api/types';
import { DEFAULT_MEMBER_OPTION } from '.';

interface Props {
  orgId: string;
  userId: string;
  memberId: string;
  memberName: string;
  memberEmail: string;
  memberOptions: Option[];
  onCancel: () => void;
  onDelete: (assignedMember: string, memberName: string) => void;
  workflows?: ReadonlyArray<Workflow> | [];
}

interface State {
  assignedMember: Option;
  typedMemberName: string;
  deleteButtonDisabled: boolean;
  memberOptions: Option[];
  assignedWorkflows: ReadonlyArray<Workflow> | [];
}

export default class DeleteModal extends React.Component<Props, State> {
  static readonly state: State

  constructor(props, context) {
    super(props, context);

    this.state = {
      assignedMember: DEFAULT_MEMBER_OPTION,
      typedMemberName: '',
      deleteButtonDisabled: true,
      memberOptions: [],
      assignedWorkflows: [],
    };
  }

  componentDidUpdate(prevProps, prevState): void {
    const { memberName } = this.props;
    const { typedMemberName, assignedMember } = this.state;

    const selectionsChanged = (prevState.assignedMember !== assignedMember)
      || (prevState.typedMemberName !== typedMemberName);

    if (selectionsChanged) {
      if ((typedMemberName !== memberName) || assignedMember === DEFAULT_MEMBER_OPTION) {
        this.setState({ deleteButtonDisabled: true });
      } else {
        this.setState({ deleteButtonDisabled: false });
      }
    }
  }

  handleMemberAssignment = (e: React.SyntheticEvent<Element, Event>, assignedMember: Option) => {
    if (assignedMember === null) {
      // do nothing
      return;
    }
    this.setState({ assignedMember });
  };

  handleNameInput = (event: string) => {
    this.setState({ typedMemberName: event });
  };

  handleDelete = () => {
    this.props.onDelete(this.state.assignedMember.value, this.props.memberName);
  };

  getAssignedWorkflows = () => {
    const { workflows, userId } = this.props;
    const memberWorkflows = filter(workflows,
      { workflow_steps: [{ step_type: 'assign_record', arguments: { assign_to: userId } }] });
    // @ts-ignore
    this.setState({ assignedWorkflows: memberWorkflows });
  }

  componentDidMount = () => {
    const memberOptions = this.props.memberOptions.filter((option) => option.value !== this.props.memberId);
    this.setState({ memberOptions });
    this.getAssignedWorkflows();
  };

  render() {
    return (
      <div className="main">
        <Alert severity="info">
          This user license will be used for the next member that you add. If
          you need to reduce your billed user license count, contact us at
          {' '}
          <Link
            href={linkTo.mail.billing({
              template: templates.billing.inquiry,
              orgId: this.props.orgId,
              userId: this.props.userId,
            })}
            target="_blank"
            rel="noopener noreferrer"
          >
            billing@fulcrumapp.com
          </Link>
        </Alert>
        <p style={{ marginBottom: 20 }}>
          You are about to delete
          {' '}
          <strong>{this.props.memberName}</strong>
          {' '}
          (
          <strong>{this.props.memberEmail}</strong>
          ) from your organization.
        </p>
        <p style={{ marginBottom: 20 }}>
          <strong>{this.props.memberName}</strong>
          {' '}
          will no longer have access to any data in your account and
          will not be able to synchronize data from a mobile device.
          <strong> Make sure all data has been synced from this user. </strong>
        </p>
        <div style={{ marginBottom: 20 }}>
          <p>
            Re-assign the records currently assigned to
            {' '}
            <strong>{this.props.memberName}</strong>
            :
          </p>
          <div>
            <SelectTypeAhead
              label=""
              data-testid="reassign-records-select"
              isError={false}
              errorResource="none"
              sx={{ width: '175px' }}
              componentsProps={{ paper: { sx: { maxHeight: '250px' } } }}
              value={this.state.assignedMember}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              options={this.state.memberOptions}
              onChange={this.handleMemberAssignment}
            />
          </div>
        </div>
        {this.state.assignedWorkflows.length ? (
          <div style={{ marginBottom: 20 }} className="alert alert-info">
            <p>
              <strong>{this.props.memberName}</strong>
              {' '}
              is a record assignee on the following workflows:
            </p>
            {this.state.assignedWorkflows.map((workflow) => (
              <div key={workflow.id}><a href={`${window.location.origin}/apps/${workflow.object_resource_id}/workflows/${workflow.resource_id}/edit`}>{workflow.name}</a></div>
            ))}
          </div>
        ) : (
          <></>
        )}
        <div className="alert alert-danger">
          <div className="clearfix delete-input">
            <p style={{ marginBottom: 10 }} className="delete-input">Please enter the name of the member to confirm.</p>
            <DelayedTextInput
              onValueChange={this.handleNameInput}
              placeholder={this.props.memberName}
              delay={500}
            />
          </div>
          <div className="left">
            <Button
              onClick={this.handleDelete}
              disabled={this.state.deleteButtonDisabled}
              destroy
            >
              Delete Member
            </Button>
          </div>
        </div>
        <div>
          <ButtonActions
            negative="Cancel"
            onCancel={this.props.onCancel}
            noPositive
          />
        </div>
      </div>
    );
  }
}
